/**
 * Central transport switchboard — the dashboard's ONE declaration of HOW the
 * REST-vs-GraphQL choice is presented and centrally governed. The choice itself
 * (which API each operation uses, and why) lives in `@authe/fe-commons/transport`.
 *
 * Five layers decide an operation's transport. Two are CEILINGS that can only
 * force REST, three are preferences in increasing precedence:
 *
 *   CEILING  — no GraphQL endpoint in this deployment (`graphqlClient == null`).
 *   CEILING  — the caller's PLAN ({@link transportCapabilitiesForPlan}): the fast
 *              path, idempotent writes over GraphQL, and per-operation control
 *              are packaged separately, so a tier can be given the default
 *              without being given the dials.
 *   ---
 *   PRODUCT  — the registry default (`TRANSPORT_REGISTRY[op].default`). Not
 *              restated here: this module used to carry a second `default` per
 *              operation, and two declarations of one fact is a drift waiting to
 *              happen. {@link transportDefaultFor} reads the registry.
 *   CENTRAL  — the plan / workspace / user preference rungs, resolved by
 *              `app/dashboardPrefs` and pushed onto `FeatureFlags`' POLICY layer
 *              by `app/transportPolicy`. This is the layer an admin manages.
 *   LOCAL    — `?debug=1` › APIs writes `graphql.<op>` flag OVERRIDES
 *              ({@link FLAG_OVERRIDE_STORAGE_KEY}). A DIAGNOSTIC, not a
 *              preference: per-browser, invisible to support, and the two paths
 *              do not resolve identically — which is why it is not in Settings.
 *              It wins over everything below so support can pin a path while
 *              reproducing something.
 *
 * Every adapter resolves through `resolveTransport`, so a change at any layer
 * takes effect app-wide with no rewiring — including inside the shared
 * `tx-table` / `tx-graph` packages.
 *
 * Granularity is per-OPERATION (the real toggle), grouped under resource-friendly
 * headers. The catalog kinds (entities/labels/tags/groups) share ONE list
 * operation (`catalog.list`), so they move together — shown as a single row.
 */
import type { FlagMap } from "@authe/fe-commons/flags";
import {
  DATA_OPERATIONS,
  GRAPHQL_MASTER_FLAG,
  TRANSPORT_CAPABILITY,
  TRANSPORT_REGISTRY,
  flagForOperation,
  type DataOperation,
  type Transport,
  type TransportCapability,
} from "@authe/fe-commons/transport";

export type { DataOperation, Transport, TransportCapability };
export { GRAPHQL_MASTER_FLAG, TRANSPORT_CAPABILITY, flagForOperation };

/**
 * localStorage key holding the `FlagMap` of local flag overrides. Matches
 * `FeatureFlags`' built-in default so existing overrides keep working; owned here
 * so the provider AND the debug panel reference the SAME key (no magic strings).
 */
export const FLAG_OVERRIDE_STORAGE_KEY = "authe:flag-overrides";

/** Resource grouping for the debug panel's transport section. */
export type TransportGroup = "transaction" | "address" | "catalog";

export interface TransportGroupMeta {
  id: TransportGroup;
  label: string;
}

/** Ordered groups (drives the transport section's layout). */
export const TRANSPORT_GROUPS: TransportGroupMeta[] = [
  { id: "transaction", label: "Transactions" },
  { id: "address", label: "Addresses" },
  { id: "catalog", label: "Catalog (entities, labels, tags & groups)" },
];

export interface TransportOperationConfig {
  op: DataOperation;
  group: TransportGroup;
  label: string;
  description: string;
  kind: "read" | "write";
  /**
   * The GraphQL path is wired but has not been soaked in production.
   *
   * Surfaced in the UI rather than left implicit, because the REST fallback makes
   * an unsoaked path indistinguishable from a proven one from the outside: a read
   * that fails every time still renders, just slower. A reader choosing GraphQL
   * here deserves to know they are the one proving it.
   */
  experimental?: boolean;
}

/**
 * How each operation is PRESENTED. Its default transport is deliberately absent:
 * that is `TRANSPORT_REGISTRY[op].default`, read through
 * {@link transportDefaultFor}.
 *
 * Keyed by `DataOperation` rather than listed, so adding an operation to the
 * registry without describing it here is a compile error instead of a row that
 * silently never appears — which is how `address.list` shipped switchable in the
 * registry and reachable from nowhere.
 *
 * Writes listed here are IDEMPOTENT only (attach/detach, set/clear, upsert/
 * delete) — GraphQL-first with a REST fallback, so a retry converges;
 * non-idempotent writes are never routed to GraphQL.
 */
export const TRANSPORT_OPERATIONS: Record<DataOperation, Omit<TransportOperationConfig, "op">> = {
  "transactions.recentFeed": {
    group: "transaction",
    label: "Recent feed (transaction table)",
    description:
      "First-paint recent-transactions feed behind the transaction table, wherever it appears. On REST the table loads from the plain list endpoint instead, with labels and entities resolved separately.",
    kind: "read",
  },
  "transactions.header": {
    group: "transaction",
    label: "Detail header",
    description:
      "First-paint header (chain, hash, finality, enrichment status) on the transaction detail page.",
    kind: "read",
  },
  "transactions.request": {
    group: "transaction",
    label: "Index an unseen transaction",
    description:
      "Turning a hash the platform has never processed into a readable transaction, when somebody pastes one into a case. Idempotent — a hash already indexed comes back as-is — and it asks for the on-chain facts only, never a decode run. On REST the ordinary detail read does the same job with a heavier round trip.",
    kind: "write",
    experimental: true,
  },
  "address.crossChain": {
    group: "address",
    label: "Cross-chain lens",
    description:
      "Every chain one address string exists on, plus the activity rollup, behind the chain switcher on an address or contract page. Both transports resolve it from the same index in a single call; GraphQL also brings each row's overlays back with it. Two fields the schema has no place for — whether the row was already stored, and whether anyone has annotated it — fall back to the same defaults REST applies when a row arrives without them.",
    kind: "read",
    experimental: true,
  },
  "address.list": {
    group: "address",
    label: "Address list",
    description:
      "The paginated list behind every address table, tenant- and entity-scoped. The cross-chain lens is a different query and has its own row below. The other address reads enrich one address at a time — this is the read that fetches the rows. Not yet soaked in production, so it stays on REST by default and falls back to REST on error. A single-address search always uses REST: this query has no argument for one, so asking it would return the whole list.",
    kind: "read",
    experimental: true,
  },
  "address.overlays": {
    group: "address",
    label: "Resolved overlays (incl. graph nodes)",
    description:
      "The effective label / tag / entity bundle for an address — on address pages, for a single node opened from a graph, and across a transaction page, where the Addresses panel and both graphs are resolved together in one request. A table's rows and the other graph surfaces stay on the REST batch whatever is chosen here, and so does any read too large to ask for in one GraphQL document: that batch is a single call where GraphQL is one resolve per address.",
    kind: "read",
  },
  "address.detail": {
    group: "address",
    label: "Detail row",
    description:
      "The merged address row + saved flag + tenancy scope switcher on the address detail page.",
    kind: "read",
  },
  "address.assignments.list": {
    group: "address",
    label: "Assignments list",
    description: "Raw catalog assignments attached to an address.",
    kind: "read",
  },
  "address.entityMapping.read": {
    group: "address",
    label: "Primary entity read",
    description: "The address's primary-entity pointer.",
    kind: "read",
  },
  "address.memberships.read": {
    group: "address",
    label: "Entity membership list",
    description:
      "An entity's authoritative members (the company map on the entity page) — addresses claimed per chain, with their role. Distinct from the primary-entity pointer, which names one entity per address.",
    kind: "read",
  },
  "address.assignments.write": {
    group: "address",
    label: "Assignment attach / detach",
    description: "Idempotent attach or detach of a catalog overlay.",
    kind: "write",
  },
  "address.primary.write": {
    group: "address",
    label: "Primary entity set / clear",
    description: "Idempotent set or clear of the primary entity.",
    kind: "write",
  },
  "address.entityMapping.write": {
    group: "address",
    label: "Entity mapping upsert / delete",
    description: "Idempotent write of the address ↔ entity mapping.",
    kind: "write",
  },
  "address.memberships.write": {
    group: "address",
    label: "Entity membership batch write",
    description:
      "Mapping many addresses onto one entity in a single call. Idempotent per member, so a retry converges; a partial write reports which members landed.",
    kind: "write",
  },
  "catalog.list": {
    group: "catalog",
    label: "Catalog lists",
    description: "List reads for entities, labels, tags & groups (one shared path).",
    kind: "read",
  },
  "catalog.item": {
    group: "catalog",
    label: "Single row by id",
    description:
      "Loading one entity / label / tag / group by its id — opening its page, and resolving the names behind already-selected members. GraphQL answers one id per request, so a handful of ids is a request each where REST reads them all in one call; past a small number this stays on REST whatever is chosen here.",
    kind: "read",
    experimental: true,
  },
  "catalog.related": {
    group: "catalog",
    label: "Related walk (“what is connected to this?”)",
    description:
      "The bounded relationship walk behind the Related panel. GraphQL-only: no REST route can reach every table it touches, so choosing REST does not make it slower — it makes the surface fall back to the flat reverse-assignment list, which is a narrower answer, not the same one.",
    kind: "read",
  },
};

/** Every configurable operation, in group order (drives the settings layout). */
export const TRANSPORT_CONFIG: TransportOperationConfig[] = DATA_OPERATIONS.map((op) => ({
  op,
  ...TRANSPORT_OPERATIONS[op],
})).sort(
  (a, b) =>
    TRANSPORT_GROUPS.findIndex((g) => g.id === a.group) -
    TRANSPORT_GROUPS.findIndex((g) => g.id === b.group),
);

/** The product default for `op` — the registry's, never a second copy. */
export function transportDefaultFor(op: DataOperation): Transport {
  return TRANSPORT_REGISTRY[op].default;
}

/** True when REST means "this read does not happen" rather than "the slow path". */
export function isGraphqlOnly(op: DataOperation): boolean {
  return TRANSPORT_REGISTRY[op].graphqlOnly === true;
}

/** Whether GraphQL is the dashboard's default (master switch). */
export const TRANSPORT_MASTER_DEFAULT = true;

// ---------------------------------------------------------------------------
// Plan ceiling
// ---------------------------------------------------------------------------

/**
 * Which transport capabilities each plan tier grants.
 *
 * A CEILING, not a default: withholding a capability holds the affected
 * operations on REST and hides their controls; it never turns anything on. That
 * asymmetry is what keeps packaging out of the business of enabling features —
 * the product default is the registry's, on every tier.
 *
 * Today every tier gets everything, so this ships as a pure no-op and the table
 * is the seam, not the policy. Tightening a tier is editing one line here; the
 * plan ids match `@authe/fe-commons/plans` (`free` / `pro` / `enterprise`), and
 * an unknown/custom plan id falls through to {@link DEFAULT_PLAN_CAPABILITIES}.
 */
export const PLAN_TRANSPORT_CAPABILITIES: Record<string, readonly TransportCapability[]> = {
  free: [
    TRANSPORT_CAPABILITY.FAST_PATH,
    TRANSPORT_CAPABILITY.WRITES,
    TRANSPORT_CAPABILITY.PER_OPERATION,
  ],
  pro: [
    TRANSPORT_CAPABILITY.FAST_PATH,
    TRANSPORT_CAPABILITY.WRITES,
    TRANSPORT_CAPABILITY.PER_OPERATION,
  ],
  enterprise: [
    TRANSPORT_CAPABILITY.FAST_PATH,
    TRANSPORT_CAPABILITY.WRITES,
    TRANSPORT_CAPABILITY.PER_OPERATION,
  ],
};

/**
 * What an unrecognised plan gets — everything.
 *
 * Deliberately permissive. A plan id we do not recognise is a gap in THIS table
 * (a tier added server-side, a bespoke enterprise contract), and answering it by
 * forcing every customer on it onto REST would turn a packaging omission into a
 * performance regression nobody could explain from the UI. The backend enforces
 * the entitlements that actually matter; this one only decides which dials the
 * client offers.
 */
export const DEFAULT_PLAN_CAPABILITIES: readonly TransportCapability[] = [
  TRANSPORT_CAPABILITY.FAST_PATH,
  TRANSPORT_CAPABILITY.WRITES,
  TRANSPORT_CAPABILITY.PER_OPERATION,
];

/**
 * The capability set for a plan id, as the resolver wants it.
 *
 * `null` / unknown id → {@link DEFAULT_PLAN_CAPABILITIES}, which also covers
 * "the subscription read has not landed yet": a plan we cannot see must not
 * briefly look like a plan that forbids things.
 */
export function transportCapabilitiesForPlan(planId: string | null | undefined): Set<string> {
  const caps = (planId && PLAN_TRANSPORT_CAPABILITIES[planId]) || DEFAULT_PLAN_CAPABILITIES;
  return new Set<string>(caps);
}

// ---------------------------------------------------------------------------
// Flag plumbing
// ---------------------------------------------------------------------------

/**
 * The `FeatureFlags.defaults` map this module contributes: the master flag, and
 * nothing else.
 *
 * Per-operation defaults are NOT seeded here any more. They were, and that made
 * the registry's own defaults unreachable — every operation carried an explicit
 * default-layer boolean, so the registry's value was dead code in this app and
 * live in every other one. Leaving the per-op flags unset lets the registry
 * answer, which is the only copy of that fact.
 */
export function transportFlagDefaults(): FlagMap {
  return { [GRAPHQL_MASTER_FLAG]: TRANSPORT_MASTER_DEFAULT };
}

/** Read the local flag-override map straight from storage (for the tri-state selects). */
export function readFlagOverrides(): FlagMap {
  try {
    if (typeof localStorage === "undefined") return {};
    return JSON.parse(localStorage.getItem(FLAG_OVERRIDE_STORAGE_KEY) ?? "{}") as FlagMap;
  } catch {
    return {};
  }
}

/**
 * The caller's explicit override for `op`, or `undefined` when none is set (the
 * "Default" state). Pure over an override map so it's testable and reused by the
 * UI without touching storage.
 */
export function transportOverrideFor(
  overrides: FlagMap,
  op: DataOperation,
): Transport | undefined {
  const value = overrides[flagForOperation(op)];
  if (value === undefined) return undefined;
  return value ? "graphql" : "rest";
}

/** The master-flag override (true/false), or `undefined` when unset. */
export function masterOverride(overrides: FlagMap): boolean | undefined {
  return overrides[GRAPHQL_MASTER_FLAG];
}
